import React, { useContext, useEffect, useRef, useState } from 'react';

export { default as DropdownButton } from './dropdownButton';
export { default as DropdownMenu } from './dropdownMenu';

const DropdownContext = React.createContext({});

type OverrideStyles = {
  [key: string]: any;
};

type DropdownPosition = 'top' | 'bottom' | 'right' | 'left';
interface DropdownProps {
  id: string | number;
  /**
   * Add additional styles
   */
  className?: string;
  /**
   * Control toggle menu out of component
   */
  onChangeToggleMenu?: (state: boolean) => void;
  /**
   * Refresh data of menu
   */
  forceRefresh?: number;
  /**
   * Default state of menu
   */
  defaultShow?: boolean;
  /**
   * Keep showing when click on menu item, default is close when click on item
   */
  keepShown?: boolean;
  // /**
  //  * Position of dropdown menu
  //  */
  placement?: DropdownPosition | undefined | null;
  // /**
  //  * override styles. You need to pass an object with css properties in camelCase
  //  */
  overrideStyles?: OverrideStyles;
  // /**
  //  * if you need the dropdown to render as a portal you should use this prop
  //  */
  renderAsPortal?: boolean,

  filterValue?: string;
  [others: string]: any;
}

interface IDropdownContext {
  id: string;
  showMenu: boolean;
  handleClickMenu: (e: React.MouseEvent<HTMLUListElement>) => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
  onToggleMenu: () => void;
  keepShown: boolean;
  setShowMenu: React.Dispatch<React.SetStateAction<boolean>>;
  placement?: DropdownPosition;
  onChangeToggleMenu: (state: boolean) => void;
  dropdownButtonRef: React.RefObject<HTMLDivElement>;
  dropdownMenuRef: React.RefObject<HTMLUListElement>;
  renderAsPortal: boolean,
  overrideStyles: OverrideStyles;
}

const Dropdown: React.FC<DropdownProps> = ({
  id,
  children,
  className,
  onChangeToggleMenu,
  forceRefresh,
  defaultShow = false,
  keepShown = false,
  placement = 'bottom',
  filterValue,
  renderAsPortal = false,
  overrideStyles = null,
  ...rest
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(defaultShow);
  const [refresh, setRefresh] = useState(forceRefresh || 0);

  // Function to toggle the dropdown visibility
  const onToggleMenu = (type?: any) => {
    setShowMenu((prev) => type === "Enter" ? false: !prev);
    if (typeof onChangeToggleMenu === 'function') {
      onChangeToggleMenu(!showMenu);
    }
  };

  useEffect(() => {
    if (showMenu) {
      const handleScroll = () => {
        setRefresh((prev) => prev + 1);
      };
      document.addEventListener('scroll', handleScroll, true);
      return () => document.removeEventListener('scroll', handleScroll);
    }
  }, [showMenu]);

  useEffect(() => {
    if (forceRefresh && refresh !== forceRefresh) {
      setRefresh(forceRefresh);
    }
  }, [forceRefresh, refresh]);

  useEffect(() => {
    if (filterValue) {
      setShowMenu(true);
      if (typeof onChangeToggleMenu === 'function') {
        onChangeToggleMenu(true);
      }
    }
  }, [filterValue]);

  useEffect(() => {
    if (defaultShow !== undefined && defaultShow !== showMenu) {
      setShowMenu(defaultShow);
    }
  }, [defaultShow]);

  const handleClickMenu = (e: React.MouseEvent<HTMLUListElement>) => {
    if (!keepShown) {
      setShowMenu(false);
      if (typeof onChangeToggleMenu === 'function') {
        onChangeToggleMenu(false);
      }
    }
  };

const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
  const code = e.code;
  const focusElement = document.activeElement as HTMLElement;
  const _menu = document.getElementById(`${id}-menu`);

  const _focusItems = _menu?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

  if (!_focusItems || _focusItems.length === 0) {
    return;
  }

  if (e.shiftKey && e.key === 'Tab') {
    if (!showMenu) {
      e.preventDefault();
      e.stopPropagation();
      onToggleMenu();
    } else if (_focusItems && focusElement) {
      e.preventDefault();
      e.stopPropagation();

      const itemsArray: HTMLElement[] = Array.from(document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
      const currentIndex: number = itemsArray.indexOf(focusElement);

      const nextIndex: number = currentIndex - 1;
      if (currentIndex !== -1 && nextIndex < itemsArray.length - 1) {
        itemsArray[nextIndex].focus();
      } else {
        itemsArray[0].focus();
      }
    }
  }
  else {
    switch (code) {
      case 'ArrowDown':
        if (!showMenu) {
          e.preventDefault();
          e.stopPropagation();
          onToggleMenu();
        } else if (_focusItems && focusElement) {
          e.preventDefault();
          e.stopPropagation();
        
          const itemsArray: HTMLElement[] = Array.from(document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
          const currentIndex: number = itemsArray.indexOf(focusElement);
          
          const nextIndex: number = currentIndex + 1;
          if (currentIndex !== -1 && nextIndex < itemsArray.length) {
            itemsArray[nextIndex].focus();
          } else {
            itemsArray[0].focus();
          }
        }
        break;
      case 'ArrowUp':
        if (!showMenu) {
          e.preventDefault();
          e.stopPropagation();
          onToggleMenu();
        } else if (_focusItems && focusElement) {
          e.preventDefault();
          e.stopPropagation();
            const currentIndex = Array.from(_focusItems).indexOf(focusElement);
            const prevIndex = currentIndex - 1;
            if (prevIndex >= 0) {
              (_focusItems[prevIndex] as HTMLElement).focus();
          } else {
              (_focusItems[_focusItems.length - 1] as HTMLElement).focus();
          }
        }
        break;
      case 'Enter':
      // Specifically handle Enter key for inputs like checkboxes
      if (focusElement && focusElement.tagName === 'INPUT' && (focusElement as HTMLInputElement).type === 'checkbox') {
          e.preventDefault(); // Prevent default to avoid any unwanted form submission behaviors
          (focusElement as HTMLInputElement).click(); // Programmatically click the checkbox to toggle it
        }
        onToggleMenu("Enter");
        break;
        case 'Tab':
          if (!showMenu) {
            e.preventDefault();
            e.stopPropagation();
            onToggleMenu();
          } else if (_focusItems && focusElement) {
            e.preventDefault();
            e.stopPropagation();
          
            const itemsArray: HTMLElement[] = Array.from(document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
            const currentIndex: number = itemsArray.indexOf(focusElement);
            
            const nextIndex: number = currentIndex + 1;
            if (currentIndex !== -1 && nextIndex < itemsArray.length) {
              itemsArray[nextIndex].focus();
            } else {
              itemsArray[0].focus();
            }
          }
          break;
      case 'Space':
        if (focusElement && focusElement.tagName === 'BUTTON') {
          e.preventDefault();
          focusElement.click();
        }
        break;
      case 'Escape':
        if (showMenu) onToggleMenu();
        break;
      default:
        break;
    }
  }
};
  
  
  return (
    <DropdownContext.Provider value={{
      id,
      showMenu,
      setShowMenu,
      handleClickMenu,
      dropdownRef,
      onToggleMenu,
      keepShown,
      placement,
      onChangeToggleMenu,
      renderAsPortal,
      overrideStyles,
    }}>
      <div ref={dropdownRef} className={`dropdown ${className || ''}`} {...rest} onKeyDown={onKeyDown}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
};

export default Dropdown;
export const useDropdown = () => useContext(DropdownContext) as IDropdownContext;
